import { useCallback, useMemo } from 'react';
import type { CollectionBase } from '@/features/events/types';
import type { PageExperienceFragment } from '@/features/experiences/fragments/PageExperience.generated';
import type {
  DecoratedMagicChat,
  DecoratedMagicJob,
} from '@/features/magic/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useChat } from './useChat';
import { useExperience } from './useExperience';
import { useJob } from './useJob';

type BaseInput = {
  chat: DecoratedMagicChat | undefined;
  job: DecoratedMagicJob | undefined;
  experience: PageExperienceFragment | undefined;
};

type Ret = {
  base: CollectionBase | null;
  setBase: (base: CollectionBase | null) => void;
};

const calcBase = ({
  chat,
  job,
  experience,
  isChatEnabled,
}: BaseInput & { isChatEnabled: boolean }): CollectionBase | null => {
  if (experience) {
    return { experience };
  }
  if (!isChatEnabled) {
    if (job) {
      return { job };
    }
    return null;
  }
  if (job && !chat?.isSaved) {
    return { job };
  }
  if (chat) {
    return { chat };
  }
  return null;
};

export const useBase = (incomingBase: BaseInput | null): Ret => {
  const { value: isChatEnabled } = useFeatureFlag('aiChatEnabled');
  const data = incomingBase && calcBase({ isChatEnabled, ...incomingBase });
  const { job, setJob } = useJob(data?.job);
  const { chat, setChat } = useChat(data?.chat);
  const { experience, setExperience } = useExperience(data?.experience);

  const setBase = useCallback(
    (base: CollectionBase | null) => {
      if (!base) {
        setJob(null);
        setChat(null);
      } else if (base.experience) {
        setExperience(base.experience);
        setJob(null);
        setChat(null);
      } else if (base.chat) {
        setJob(null);
        setExperience(null);
        setChat(base.chat);
      } else if (base.job) {
        setJob(base.job);
        setChat(null);
        setExperience(null);
      }
    },
    [setChat, setJob, setExperience]
  );

  const base: CollectionBase | null = useMemo(() => {
    return experience ? { experience } : chat ? { chat } : job ? { job } : null;
  }, [experience, chat, job]);

  return {
    base,
    setBase,
  };
};
